import { GlobalStyles, useTheme } from "@mui/material";

export default function RsuiteDateRangePickerWrapper() {
  const theme = useTheme();

  return (
    <>
      <GlobalStyles
        styles={{
          /* Year & Month selection overlay styling */
          ".rs-calendar-month-dropdown, .rs-calendar-year-dropdown": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
          },

          // Picker toggle input background and text
          ".rs-picker-toggle": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            height: "56px", // Match MUI TextField height (small size)
            minHeight: "56px",
            borderRadius: "4px", // Match MUI border radius
            fontSize: "0.875rem", // Match MUI TextField font size
            padding: "8px 12px", // Match MUI TextField padding
            display: "flex",
            alignItems: "center",
            "&:hover": {
              borderColor: theme.palette.primary.main,
            },
            "&.rs-picker-toggle-active": {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
            },
          },
          ".rs-picker-toggle-placeholder": {
            color: theme.palette.text.secondary,
            fontSize: "0.875rem",
          },
          ".rs-picker-toggle-value": {
            color: theme.palette.text.primary,
            fontSize: "0.875rem",
          },
          ".rs-picker-toggle-caret, .rs-picker-toggle-clean": {
            color: theme.palette.text.secondary,
            fontSize: "1rem",
          },
          ".rs-picker-toggle-clean:hover": {
            color: theme.palette.error.main,
          },

          // Picker dropdown menu - ensure it appears above sticky headers (z-index 10)
          ".rs-picker-menu": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
            borderRadius: "4px",
            zIndex: "1400 !important", // Higher than sticky headers (10) and MUI modals (1300)
          },
          ".rs-picker-popup": {
            zIndex: "1400 !important",
          },
          ".rs-picker-daterange-popup": {
            zIndex: "1400 !important",
          },
          ".rs-picker-daterange-menu": {
            zIndex: "1400 !important",
          },
          ".rs-calendar": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
          ".rs-calendar-header": {
            backgroundColor: theme.palette.background.paper,
          },
          ".rs-calendar-header-title": {
            color: theme.palette.text.primary,
            fontWeight: 500,
            fontSize: "0.875rem",
          },
          ".rs-calendar-header-forward, .rs-calendar-header-backward": {
            color: theme.palette.text.primary,
            borderRadius: 4,
            fontSize: "1rem",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.08)",
              color: theme.palette.text.primary,
            },
          },
          ".rs-calendar-table-cell-content": {
            color: theme.palette.text.primary,
            fontSize: "0.875rem",
          },
          ".rs-picker-daterange-header": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : "#fff",
            color: theme.palette.text.primary,
          },
          
          // Selection highlight
          ".rs-calendar-table-cell.rs-calendar-table-cell-selected .rs-calendar-table-cell-content": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },

          // In-range highlight
          ".rs-calendar-table-cell.rs-calendar-table-cell-in-range .rs-calendar-table-cell-content": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(25, 118, 210, 0.2)"
                : "rgba(25, 118, 210, 0.1)",
          },

          // Today border
          ".rs-calendar-table-cell.rs-calendar-table-cell-today .rs-calendar-table-cell-content": {
            border: `1px solid ${theme.palette.primary.main}`,
          },

          // Disabled dates
          ".rs-calendar-table-cell.rs-calendar-table-cell-disabled .rs-calendar-table-cell-content": {
            color: theme.palette.text.disabled,
          },

          // Toolbar background and borders
          ".rs-picker-toolbar": {
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
          },

          // Buttons
          ".rs-btn": {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
            },
          },
          ".rs-btn.rs-btn-primary": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            border: `1px solid ${theme.palette.primary.main}`,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          },
          ".rs-btn.rs-btn-disabled": {
            color: theme.palette.text.disabled,
            backgroundColor: theme.palette.action.disabledBackground,
            border: `1px solid ${theme.palette.divider}`,
          },

          // Theme-aware scrollbars for rsuite components
          ".rs-picker-menu::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          ".rs-picker-menu::-webkit-scrollbar-track": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            borderRadius: "4px",
          },
          ".rs-picker-menu::-webkit-scrollbar-thumb": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.5)",
            },
          },
          ".rs-calendar::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          ".rs-calendar::-webkit-scrollbar-track": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
            borderRadius: "4px",
          },
          ".rs-calendar::-webkit-scrollbar-thumb": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
      />
    </>
  );
}
